import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from 'formik';
import DropdownSelector from "../../../../Common/dropdown-selector";
import { updateToolValidationSchema } from './update-tool.validation';
import { UpdateToolFormValues, LinkedProduct } from './update-tool.types';
import { DropdownOption } from "../../../../../common/types/app.types";
import PlugInIcon from "../../../../icons/plug-in.icon";
import cn from "../../../../../Utils/class-names.utils";
import { PracticeTypeListItem, TechnologyListItem } from "../../../../../common/types/tool.types";
import useGetToolProducts from "../../get-tool-products.data";
import integrationUtils from "../../../../../Utils/integration.utils";
import TechnologiesList from "./technologies-list.component";
import AddTechnologyDropdown from "./add-technology-dropdown.component";
import ProductLicensesTable from "./product-licenses-table.component";
import PracticeTypesList from "./practice-types-list.component";
import AddPracticeTypeDropdown from "./add-practice-type-dropdown.component";
import CreateLicensesSection from "./create-licenses.section";
import "./update-tool.form.scss";
import UnplugIcon from "../../../../icons/unplug.icon";
import LinkedProductsSelector from "../../linked-products-selector.component";

interface UpdateToolFormProps {
  initialValues: UpdateToolFormValues;
  monitoringOptions: DropdownOption[];
  technologiesList: TechnologyListItem[];
  practiceTypesList: PracticeTypeListItem[];
  isNewTool: boolean;
  showPracticeTypes: boolean;
  showToolInfo: boolean;
  showCreateLicenses: boolean;
  technologiesTitle: string;
  isView: boolean;
  onSubmit: (values: UpdateToolFormValues) => Promise<void> | void;
  onValidChange: (isValid: boolean) => void;
}

const UpdateToolForm: React.FC<UpdateToolFormProps> = ({
  initialValues: initialValuesProp,
  monitoringOptions,
  technologiesList,
  practiceTypesList,
  isNewTool,
  showPracticeTypes,
  showToolInfo,
  showCreateLicenses,
  technologiesTitle,
  isView,
  onSubmit,
  onValidChange,
}) => {
  const formInitialValues: UpdateToolFormValues = initialValuesProp;

  return (
    <div className="update-tool-form">
      <Formik
        initialValues={formInitialValues}
        enableReinitialize
        validationSchema={updateToolValidationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (isView) {
              setSubmitting(false);
              return;
            }
            await onSubmit(values);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, setFieldValue, isValid }) => (
          <FormComponent 
            values={values}
            isValid={isValid}
            monitoringOptions={monitoringOptions}
            technologiesList={technologiesList}
            practiceTypesList={practiceTypesList}
            isNewTool={isNewTool}
            showPracticeTypes={showPracticeTypes}
            showToolInfo={showToolInfo}
            showCreateLicenses={showCreateLicenses}
            technologiesTitle={technologiesTitle}
            onValuesChange={onSubmit}
            onValidChange={onValidChange}
            setFieldValue={setFieldValue}
            isView={isView}
          />
        )}
      </Formik>
    </div>
  );
};

export default UpdateToolForm;


interface FormComponentProps {
  values: UpdateToolFormValues;
  isValid: boolean;
  monitoringOptions: DropdownOption[];
  technologiesList: DropdownOption[];
  practiceTypesList: PracticeTypeListItem[];
  isNewTool: boolean;
  showPracticeTypes: boolean;
  showToolInfo: boolean;
  showCreateLicenses: boolean;
  technologiesTitle: string;
  onValuesChange: (values: UpdateToolFormValues) => void;
  onValidChange: (isValid: boolean) => void;
  setFieldValue: (field: string, value: any) => void;
  isView: boolean;
}

const FormComponent = ({ 
  values, isValid, monitoringOptions, technologiesList, practiceTypesList, isNewTool, showPracticeTypes, 
  showToolInfo, showCreateLicenses, technologiesTitle, onValuesChange, onValidChange, setFieldValue, isView
}: FormComponentProps) => {
  const [searchProductValue, setSearchProductValue] = useState("");
  const { data: products, fetchProducts, isLoading: isProductsLoading } = useGetToolProducts() as any;
  const LinkedProductsSelectorTyped = LinkedProductsSelector as any;
  const [showAddProductModalConfig, setShowAddProductModalConfig] = useState({
    show: false,
    hideModal: () => {
      setShowAddProductModalConfig({ ...showAddProductModalConfig, show: false });
    },
    tool: null,
    selectedProductId: 0,
    newProductName: "",
  });

  useEffect(() => {
    onValidChange(isValid);
  }, [isValid]);

  useEffect(() => {
    onValuesChange(values);
  }, [values]);

  const mapProductsList = (productsList: any[] | undefined) => {
    return productsList?.map((item: any) => ({
      ...item,
      ObjectId: item.ObjId_PSAProduct,
      Name: item.ProductName,
      Icon: integrationUtils.getImagePath(item.IntegrationId)
    })) || [];
  };

  const filteredTechnologiesList = useMemo(() => {
    return technologiesList?.filter((item) => !values.technologiesList.some((t) => t.value == item.value)) || [];
  }, [technologiesList, values.technologiesList]);

  const selectedTechnologiesCount = useMemo(() => {
    return values.technologiesList?.filter((t) => t.selected).length || 0;
  }, [values.technologiesList]);

  const canSelectMoreTechnologies = !isNewTool || selectedTechnologiesCount < 1;

  const filteredPracticeTypesList = useMemo(() => {
    return practiceTypesList?.filter((item) => !values.practiceTypesList?.some((t) => t.value == item.value)) || [];
  }, [practiceTypesList, values.practiceTypesList]);

  const handleProductSearch = (searchValue: string) => {
    if (isView) return;
    fetchProducts({ pageNumber: 0, searchValue });
    setSearchProductValue(searchValue);
  };

  const mapSelectedList = (linkedProducts: LinkedProduct[] | undefined) => {
    return linkedProducts?.map(item => ({
      ...item,
      ObjectId: item.id,
      Name: item.name
    })) || [];
  };

  const handleProductDropdownOpen = () => {
    if (isView) return;
    if (isProductsLoading) return;
    fetchProducts({ pageNumber: 0, searchValue: searchProductValue });
  };

  const handleLoadMoreProducts = () => {
    return new Promise<void>((resolve) => {
      if (isView) return resolve();
      if (isProductsLoading || products.IsLastPage) return resolve();
      fetchProducts({ pageNumber: products?.PageNumber, searchValue: searchProductValue })
        .finally(() => {
          resolve();
        });
    });
  };

  const handleAddProduct = async (item: any) => {
    if (isView) return;
    const newProduct: LinkedProduct = {
      ...item,
      id: item.ObjectId.toString(),
      name: item.Name,
    };
    setFieldValue('linkedProducts', [...values.linkedProducts, newProduct]);
    setSearchProductValue('');
    if (newProduct?.ProductLicenseVM?.IsLicensed) {
      const quantity = newProduct?.ProductLicenseVM?.ProductLicense_List?.[0]?.LicenseQuantity || 0;
      const cost = newProduct?.Cost || 0;
      const totalCost = (quantity * cost).toFixed(2);
      setFieldValue('productLicenses', [...values.productLicenses, {
        id: newProduct.id,
        name: newProduct.name,
        quantity: quantity,
        pricePerUnit: `${newProduct?.CurrencyCode_Selected?.CurrencySymbol} ${totalCost} /${newProduct?.BillingPeriod_Text}`,
      }]);
    }
  };

  const handleRemoveProduct = async (item: any) => {
    if (isView) return;
    const newProducts = values.linkedProducts.filter(p => p.id !== item.id);
    setFieldValue('linkedProducts', newProducts);
    const newProductLicenses = values.productLicenses.filter(pl => pl.id !== item.id);
    setFieldValue('productLicenses', newProductLicenses);
  };

  const handleSetIsOpenState = (isOpen: boolean) => {
    
  };

  const handleShowMoreClick = () => {};

  const handleAddNewProduct = (tool: any, productId: any, search: string) => {
    if (isView) return;
    setShowAddProductModalConfig({ ...showAddProductModalConfig, show: true, tool: tool, selectedProductId: productId, newProductName: search });
  };

  const handlePracticeTypeSelectionChange = (practiceId: any, practiceTypeId: any, isSelected: boolean) => {
    if (isView) return;
    const updatedPracticeTypesList = values.practiceTypesList.map((practice) => {
      if (practice.value === practiceId) {
        return {
          ...practice,
          practiceTypes: practice.practiceTypes.map((practiceType) => {
            if (practiceType.value === practiceTypeId) {
              return {
                ...practiceType,
                selected: isSelected,
              };
            }
            return practiceType;
          }),
        };
      }
      return practice;
    });
    setFieldValue('practiceTypesList', updatedPracticeTypesList);
  };

  return (
    <Form className="d-flex flex-column gap-16 w-100">
      {isNewTool && (
        <>
          <div className="d-flex flex-column gap-4">
            <label htmlFor="toolName" className="update-tool-form__label">
              Tool Name
            </label>
            <input 
              type="text"
              id="toolName"
              name="toolName"
              placeholder="Tool name"
              className="blue-border-focus radius-4 input-wrapper"
              aria-label="Tool name"
              value={values.toolName}
              onChange={(e) => {
                if (isView) return;
                setFieldValue('toolName', e.target.value);
              }}
              readOnly={isView}
            />
          </div>
          <div className="d-flex flex-column gap-4">
            <label htmlFor="toolName" className="update-tool-form__label">
              Website
            </label>
            <input 
              type="text"
              id="website"
              name="website"
              placeholder="Website"
              className="blue-border-focus radius-4 input-wrapper"
              aria-label="Website"
              value={values.website}
              onChange={(e) => {
                if (isView) return;
                setFieldValue('website', e.target.value);
              }}
              readOnly={isView}
            />
          </div>
        </>
      )}
      {showToolInfo && (
        <>
          <div className="update-tool-form__form">
            <fieldset className="update-tool-form__fieldset">
              <legend className="sr-only">Connection Status</legend>
              <label
                htmlFor="status-display"
                className="update-tool-form__label"
              >
                Status
              </label>
              <div className="update-tool-form__status">
                <div
                  id="status-display"
                  className="update-tool-form__status-display"
                  role="status"
                  aria-label="Connection status: Connected"
                >
                  <div className={cn("update-tool-form__status-badge", {
                    'connected': values.isConnected,
                  })}>
                    <div className="update-tool-form__status-badge-icon">
                      {values.isConnected ? <PlugInIcon /> : <UnplugIcon />}
                    </div>
                    <div className="update-tool-form__status-badge-text">
                      {values.isConnected ? 'Connected' : 'Not Connected'}
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
            <fieldset className="update-tool-form__fieldset">
              <legend className="sr-only">Reason</legend>
              <label
                htmlFor="reason"
                className="update-tool-form__label"
              >
                Reason
              </label>
              <div className="update-tool-form__input-wrapper">
                <Field
                  type="text"
                  id="reason"
                  name="reason"
                  placeholder="Reason here"
                  className="update-tool-form__input"
                  aria-label="Reason for connection"
                  readOnly={isView}
                  disabled={isView}
                />
              </div>
            </fieldset>
            <fieldset className="update-tool-form__fieldset">
              <legend className="sr-only">Monitoring Settings</legend>
              <label
                htmlFor="monitoringOption"
                className="update-tool-form__label"
              >
                Monitored by SOC
              </label>
              <div className="update-tool-form__select-wrapper">
                <DropdownSelector
                  list={monitoringOptions}
                  selected={{ value: values.monitoringOption, label: values.monitoringOption }}
                  onItemSelected={(item) => {
                    if (isView) return;
                    setFieldValue('monitoringOption', item.value);
                  }}
                  className="w-100"
                  toggleClassName="d-flex align-items-center gap-8 justify-content-between w-100"
                  disabled={isView}
                >
                  <span className="f-black">
                    {values.monitoringOption}
                  </span>
                  {!isView && (
                    <div className="arrow-icon mr-2" />
                  )}
                </DropdownSelector>
              </div>
            </fieldset>
            <fieldset className="update-tool-form__fieldset update-tool-form__fieldset--vertical">
              <legend className="sr-only">Linked Products</legend>
              <div className="update-tool-form__label">
                Linked Products
              </div>
              <div className="update-tool-form__linked-products">
                <LinkedProductsSelectorTyped
                  tool={{}}
                  list={mapProductsList(products?.PSAProductVM_List)}
                  isLoading={isProductsLoading}
                  onSearch={handleProductSearch}
                  selectedList={mapSelectedList(values.linkedProducts)}
                  onOpen={handleProductDropdownOpen}
                  onLoadMore={handleLoadMoreProducts}
                  onAdd={handleAddProduct}
                  onRemove={handleRemoveProduct}
                  setIsOpenState={handleSetIsOpenState}
                  onShowMoreClick={handleShowMoreClick}
                  onAddNewProduct={handleAddNewProduct}
                  isView={isView}
                />
              </div>
            </fieldset>
          </div>
          <ProductLicensesTable 
            productLicenses={values.productLicenses} 
            onAddNewProduct={isView ? undefined : handleAddNewProduct}
          />
        </>
      )}
      <section className="update-tool-form__section">
        <div className="update-tool-form__section-header">
          <h3 className="update-tool-form__section-title">
            {technologiesTitle}
          </h3>
          <p className="update-tool-form__section-description">
            For your convenience our system has prefilled the most common usage
            for this tool.
          </p>
        </div>
        <div className={cn("update-tool-form__table", {
          'with-practice-types': showPracticeTypes,
        })}>
          <div className="update-tool-form__technologies-dropdown">
            <h4 className="update-tool-form__table-title m-0">
              Technologies
            </h4>
            <TechnologiesList
              technologiesList={values.technologiesList}
              isNewTool={isNewTool}
              selectedTechnologiesCount={selectedTechnologiesCount}
              setFieldValue={setFieldValue}
              values={values}
              isView={isView}
            />
            {!isView && (
              <AddTechnologyDropdown
                filteredTechnologiesList={filteredTechnologiesList as TechnologyListItem[]}
                canSelectMoreTechnologies={canSelectMoreTechnologies}
                values={values}
                setFieldValue={setFieldValue}
              />
            )}
          </div>
          {showPracticeTypes && (
            <div className="update-tool-form__technologies-dropdown">
              <h4 className="update-tool-form__table-title m-0">
                Practice Types
              </h4>
              <PracticeTypesList
                practicesList={values.practiceTypesList}
                search=""
                onPracticeTypeSelectionChange={handlePracticeTypeSelectionChange}
                isView={isView}
              />
              {!isView && (
                <AddPracticeTypeDropdown
                  filteredPracticeTypesList={filteredPracticeTypesList}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              )}
            </div>
          )}
        </div>
      </section>
      {showCreateLicenses && values.licenseCreation && (
        <CreateLicensesSection 
          monitoringOptions={monitoringOptions}
          licenseCreation={values.licenseCreation}
          monitoringOption={values.monitoringOption}
          setFieldValue={setFieldValue}
          isView={isView}
        />
      )}
     
    </Form>
  )
}
